import React, { useEffect, useState } from 'react';
import { Button, ConfigProvider, Input, Radio } from 'antd';
import { isDevAuthMock } from '@ext/auth/index';
import { EchoLogo } from '@ext/components/Echo';
import { BRAND } from '@ext/components/billing/brand';
import {
  type CancelRequest,
  type CancelResult,
  cancelByEmail,
} from '@ext/components/billing/billingApi';
import { antThemeConfig } from '@ext/theme/index';

/**
 * "Verträge hier kündigen" (§312k BGB): the public cancel page at /kuendigen. Reachable without
 * signing in, German, plain: name, email address, the contract, the kind of cancellation
 * (ordinary, or extraordinary with its reason), when it should take effect, and one button. The
 * server answers the same way whether or not the address has an account.
 */

export const CANCEL_PATH = '/kuendigen';

/** Whether the address is the cancel page's (with or without a trailing slash). */
export function isCancelPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').toLowerCase() === CANCEL_PATH;
}

/** "25. Oktober 2026 um 14:30 Uhr", in German time. */
export function formatReceived(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const text = date.toLocaleString('de-DE', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  });
  return `${text} Uhr`;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT = 'contact@lingoping.com';

const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 6 };
const errorStyle: React.CSSProperties = { color: '#b91c1c', margin: '6px 0 0', fontSize: 14 };

interface Props {
  cancel?: (request: CancelRequest) => Promise<CancelResult>;
  /** The simulated development sign-in has no server: the form only pretends. */
  devMock?: boolean;
}

/** Today in German time, as YYYY-MM-DD (the earliest day a cancellation can name). */
function todayInBerlin(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' });
}

export const CancelPage: React.FC<Props> = ({
  cancel = cancelByEmail,
  devMock = isDevAuthMock(),
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<CancelRequest['kind']>('ordinary');
  const [reason, setReason] = useState('');
  const [timing, setTiming] = useState<'next_possible' | 'date'>('next_possible');
  const [date, setDate] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    reason?: string;
    date?: string;
    form?: string;
  }>({});
  const [sending, setSending] = useState(false);
  const [receivedAt, setReceivedAt] = useState<string | null>(null);

  useEffect(() => {
    const previous = { title: document.title, lang: document.documentElement.lang };
    document.title = 'Verträge hier kündigen – LingoPing';
    document.documentElement.lang = 'de';
    return () => {
      document.title = previous.title;
      document.documentElement.lang = previous.lang;
    };
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Bitte geben Sie Ihren Namen ein.';
    if (!EMAIL.test(email.trim())) next.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    if (kind === 'extraordinary' && !reason.trim()) {
      next.reason = 'Bitte nennen Sie den Grund für die außerordentliche Kündigung.';
    }
    if (timing === 'date' && !(/^\d{4}-\d{2}-\d{2}$/.test(date) && date >= todayInBerlin())) {
      next.date = 'Bitte wählen Sie ein Datum ab heute.';
    }
    setErrors(next);
    if (next.name || next.email || next.reason || next.date) return;

    setSending(true);
    try {
      // The simulated development sign-in has no server to send this to.
      const result = devMock
        ? { ok: true as const, received_at: new Date().toISOString() }
        : await cancel({
            email: email.trim(),
            name: name.trim(),
            kind,
            ...(kind === 'extraordinary' ? { reason: reason.trim().slice(0, 1000) } : {}),
            effective: timing === 'date' ? date : 'next_possible',
          });
      setReceivedAt(result.received_at);
    } catch (err) {
      console.error('Cancel request failed:', err);
      setErrors({
        form: `Das hat nicht geklappt. Bitte versuchen Sie es noch einmal oder schreiben Sie an ${CONTACT}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <ConfigProvider theme={antThemeConfig}>
      <div
        lang="de"
        style={{
          minHeight: '100vh',
          boxSizing: 'border-box',
          padding: '24px 16px 48px',
          background: '#f1f5f9',
          fontFamily: antThemeConfig.token?.fontFamily,
          color: '#0f172a',
        }}
      >
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <a
            href="https://lingoping.com"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 20,
              color: BRAND.deep,
              fontWeight: 700,
              fontSize: 18,
              textDecoration: 'none',
            }}
          >
            <EchoLogo size={32} />
            LingoPing
          </a>

          <main
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '24px 20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            }}
          >
            <h1 style={{ margin: '0 0 16px', fontSize: 26, lineHeight: 1.25 }}>
              Verträge hier kündigen
            </h1>

            {receivedAt ? (
              <p role="status" style={{ fontSize: 17, lineHeight: 1.5, margin: 0 }}>
                Ihre Kündigung ist eingegangen am {formatReceived(receivedAt)}. Wir haben Ihnen eine
                Bestätigung per E-Mail geschickt.
              </p>
            ) : (
              <form onSubmit={submit} noValidate>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="cancel-name" style={labelStyle}>
                    Name
                  </label>
                  <Input
                    id="cancel-name"
                    size="large"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'cancel-name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="cancel-name-error" role="alert" style={errorStyle}>
                      {errors.name}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="cancel-email" style={labelStyle}>
                    E-Mail-Adresse
                  </label>
                  <Input
                    id="cancel-email"
                    size="large"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'cancel-email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="cancel-email-error" role="alert" style={errorStyle}>
                      {errors.email}
                    </p>
                  )}
                </div>

                <dl style={{ margin: '0 0 16px' }}>
                  <dt style={{ fontWeight: 600 }}>Vertrag</dt>
                  <dd style={{ margin: 0 }}>LingoPing Monatsabo</dd>
                </dl>

                <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
                  <legend style={labelStyle}>Art der Kündigung</legend>
                  <Radio.Group
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                  >
                    <Radio value="ordinary">Ordentliche Kündigung</Radio>
                    <Radio value="extraordinary">Außerordentliche Kündigung</Radio>
                  </Radio.Group>
                  {kind === 'extraordinary' && (
                    <div style={{ marginTop: 10 }}>
                      <label htmlFor="cancel-reason" style={labelStyle}>
                        Grund
                      </label>
                      <Input.TextArea
                        id="cancel-reason"
                        rows={3}
                        maxLength={1000}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        aria-invalid={!!errors.reason}
                        aria-describedby={errors.reason ? 'cancel-reason-error' : undefined}
                      />
                      {errors.reason && (
                        <p id="cancel-reason-error" role="alert" style={errorStyle}>
                          {errors.reason}
                        </p>
                      )}
                    </div>
                  )}
                </fieldset>

                <fieldset style={{ border: 0, padding: 0, margin: '0 0 20px' }}>
                  <legend style={labelStyle}>Kündigungszeitpunkt</legend>
                  <Radio.Group
                    value={timing}
                    onChange={(e) => setTiming(e.target.value)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                  >
                    <Radio value="next_possible">Zum nächstmöglichen Zeitpunkt</Radio>
                    <Radio value="date">Zu einem bestimmten Datum</Radio>
                  </Radio.Group>
                  {timing === 'date' && (
                    <div style={{ marginTop: 10 }}>
                      <label htmlFor="cancel-date" style={labelStyle}>
                        Datum
                      </label>
                      <Input
                        id="cancel-date"
                        type="date"
                        size="large"
                        min={todayInBerlin()}
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        aria-invalid={!!errors.date}
                        aria-describedby={errors.date ? 'cancel-date-error' : undefined}
                      />
                      {errors.date && (
                        <p id="cancel-date-error" role="alert" style={errorStyle}>
                          {errors.date}
                        </p>
                      )}
                    </div>
                  )}
                </fieldset>

                {errors.form && (
                  <p role="alert" style={{ ...errorStyle, margin: '0 0 12px' }}>
                    {errors.form}
                  </p>
                )}

                <Button type="primary" htmlType="submit" size="large" block loading={sending}>
                  Jetzt kündigen
                </Button>
              </form>
            )}
          </main>
        </div>
      </div>
    </ConfigProvider>
  );
};
